import { useState, useEffect } from 'react';
import { useFavorites, type FavoriteQuestion } from '../../hooks/useFavorites';
import { normalizeCorrectAnswer } from '../../utils/answerNormalization';

interface QuestionCardProps {
  question: FavoriteQuestion;
  index: number;
  showAnswersByDefault?: boolean;
  showDeleteButton?: boolean;
}

export default function QuestionCard({ question, index, showAnswersByDefault = false, showDeleteButton = false }: QuestionCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [selectedOpt, setSelectedOpt] = useState<number | null>(null);
  const [isFlagged, setIsFlagged] = useState<boolean>(false);

  const isFav = isFavorite(question.id);
  const correctOpt = normalizeCorrectAnswer(question.correct_answer);
  const hasAnswered = selectedOpt !== null;
  const showAnswers = showAnswersByDefault || hasAnswered;

  useEffect(() => {
    const handleFlagged = (e: any) => {
      if (e.detail?.questionId === question.id) {
        setIsFlagged(true);
      }
    };
    window.addEventListener('question-flagged' as any, handleFlagged);
    return () => window.removeEventListener('question-flagged' as any, handleFlagged);
  }, [question.id]);

  return (
    <div className="question-card relative transition-all duration-300 ease-in-out bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-white/10 rounded-3xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.05)] space-y-5 text-left">
      <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          {/* Index Box */}
          <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold relative flex-shrink-0">
            {index + 1}
          </div>
          <div className="flex-1 space-y-1.5">
            <h4 className="font-bangla text-base sm:font-semibold font-normal text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed pt-0.5 whitespace-pre-line">
              {question.question_text}
            </h4>
          </div>
        </div>
        
        {/* Action Icon Group - Apple-inspired balanced layout */}
        <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
          {/* Flag Button */}
          <button 
            onClick={() => (window as any).showReportModal?.(question.id)}
            className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${isFlagged ? 'text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20' : 'text-slate-400 hover:text-teal-500 hover:bg-teal-50 dark:hover:text-[#00CBA9] dark:hover:bg-teal-950/20'}`}
            title="রিপোর্ট করুন"
          >
            <svg className="w-5 h-5" fill={isFlagged ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7"/>
            </svg>
          </button>
          
          {/* Heart/Favorite Button */}
          <button 
            onClick={() => toggleFavorite(question)}
            className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${isFav ? 'text-rose-500 bg-rose-50 dark:bg-rose-950/20' : 'text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:text-rose-500 dark:hover:bg-rose-950/20'}`}
            title="পছন্দ করুন"
          >
            <svg className="w-5 h-5" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
            </svg>
          </button>

          {/* Destructive Action Partition */}
          {showDeleteButton && (
            <>
              <div className="h-5 w-[1px] bg-slate-200 dark:bg-white/10 mx-1.5" />
              <button 
                onClick={(e) => {
                  if ((window as any).singleClickDelete) {
                    (window as any).singleClickDelete(e.currentTarget, question.id);
                  }
                }}
                className="delete-mistake-btn text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all duration-200 rounded-xl p-1.5 cursor-pointer bg-transparent border-none"
                title="স্থায়ীভাবে মুছুন"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6"/>
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {question.stem && (
        <div className="p-4 rounded-2xl bg-[#F4F7FB] dark:bg-white/5 border-l-4 border-l-blue-500 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-bangla whitespace-pre-line ml-0 md:ml-12">
          <strong className="block text-slate-550 dark:text-slate-400 mb-1">উদ্দীপক:</strong>
          {question.stem}
        </div>
      )}

      {/* Options */}
      <div className="grid grid-cols-1 gap-3 pl-0 md:pl-12">
        {question.options && question.options.map((option: string, optionIndex: number) => {
          const isSelected = selectedOpt === optionIndex;
          const isCorrect = optionIndex === correctOpt;

          let optionStyle = 'border-gray-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:border-[#00CBA9]/30 dark:hover:border-white/15 hover:bg-white dark:hover:bg-white/10 hover:shadow-sm';
          let badgeStyle = 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400';

          if (showAnswers) {
            if (isCorrect) {
              optionStyle = 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
              badgeStyle = 'bg-emerald-500 text-white';
            } else if (isSelected) {
              optionStyle = 'border-rose-500 bg-rose-500/10 text-rose-700 dark:text-rose-400';
              badgeStyle = 'bg-rose-500 text-white';
            }
          } else if (isSelected) {
            optionStyle = 'border-[#00CBA9] bg-[#00CBA9]/5 text-[#00CBA9] dark:text-[#8AB4F8]';
            badgeStyle = 'bg-[#00CBA9] text-white';
          }

          return (
            <button
              key={optionIndex}
              onClick={() => {
                if (!showAnswers) {
                  setSelectedOpt(optionIndex);
                }
              }}
              className={`group flex items-center p-3.5 rounded-2xl border transition-all text-left w-full cursor-pointer ${optionStyle}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bangla text-xs font-bold mr-3.5 flex-shrink-0 transition-colors ${badgeStyle}`}>
                {['ক', 'খ', 'গ', 'ঘ'][optionIndex]}
              </div>
              <span className="font-bangla text-sm sm:font-medium font-normal leading-relaxed text-[#1F1F1F] dark:text-[#E8EAED]">
                {option}
              </span>
            </button>
          );
        })}
      </div>
      
      {question.explanation && showAnswers && (
        <div className="p-4 rounded-2xl bg-emerald-50/50 dark:bg-emerald-500/5 border border-emerald-100 dark:border-emerald-500/10 text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300 font-bangla ml-0 md:ml-12">
          <strong className="text-emerald-600 dark:text-emerald-400 block mb-1">ব্যাখ্যা:</strong>
          {question.explanation}
        </div>
      )}

    </div>
  );
}
